import React, { createContext, useState } from 'react';
import { EditItemMapTrackingProps } from '..'; 
import { isServer } from '@xingternal/hops';

type EditContextType = {
  isEditing: boolean | undefined;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  isEditor: boolean;
  setIsEditor: React.Dispatch<React.SetStateAction<boolean>>;
  isAdmin: boolean;
  setIsAdmin: React.Dispatch<React.SetStateAction<boolean>>;
  trackingData: EditItemMapTrackingProps | null;
  setTrackingData: React.Dispatch<React.SetStateAction<EditItemMapTrackingProps | null>>;
}

export type EditContextValidatedType = Omit<EditContextType, 'isEditing|setIsEditing'> & { 
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  isAdmin: boolean;
  setIsAdmin: React.Dispatch<React.SetStateAction<boolean>>;
};

type EditContextProviderType = {
  children?: React.ReactNode;
  initialValue?: {
    isEditing?: boolean;
    isEditor?: boolean;
    isAdmin?: boolean;
  };
}

export const EditContext = createContext<EditContextType>(
  {
    isEditing: undefined,
    setIsEditing: () => {},
    isEditor: false,
    setIsEditor: () => {},
    isAdmin: false,
    setIsAdmin: () => {},
    trackingData: null,
    setTrackingData: () => {},
  }
);

export const EditContextProvider = ({ children, initialValue }: EditContextProviderType) => {
  const [isEditing, setIsEditing] = useState<boolean | undefined>(initialValue?.isEditing ?? (isServer ? false : undefined));
  const [isEditor, setIsEditor] = useState(initialValue?.isEditor ?? false);
  const [isAdmin, setIsAdmin] = useState(initialValue?.isAdmin ?? false);
  const [trackingData, setTrackingData] = useState<EditItemMapTrackingProps | null>(null);

  return (
    <EditContext.Provider
      value={{
        isEditing,
        setIsEditing,
        isEditor,
        setIsEditor,
        isAdmin,
        setIsAdmin,
        trackingData,
        setTrackingData,
      }}
    >
      {children}
    </EditContext.Provider>
  );
};

export const useEditContext = (): EditContextValidatedType => {
  const context = React.useContext(EditContext);

  if (!context || typeof context.isEditing === 'undefined') {
    throw new Error('No host context found. Please setup the EditContext');
  }

  return context as EditContextValidatedType;
};
